/* =============================================================================
 * Field / TextInput — APS primitives (Track D, added for KGH-795)
 * -----------------------------------------------------------------------------
 * The portal had primitives for every surface a form sits ON (Card, Button,
 * Pill) and none for the form itself, so every screen hand-styled its own
 * label + input + error row. These two close that gap and are the controls the
 * college-coach sign-up and recruiting-profile forms are built from.
 *
 * Accessibility is the point of `Field`, not decoration: it owns the ids, so a
 * caller cannot forget to tie the label, the hint and the error message to the
 * control. Pass the same id to both and the wiring is done:
 *
 *   <Field id="coach-name" label="Your name" error={errors.name} hint="...">
 *     <TextInput {...fieldAria('coach-name', { hint: true, error: errors.name })} />
 *   </Field>
 *
 * Focus and invalid borders come from the `.aps-input` rule in globals.css —
 * inline styles cannot express :focus.
 *
 * WHICH IS WHY NEITHER CONTROL BELOW SETS `outline` INLINE. An inline
 * declaration beats an author stylesheet rule whatever its specificity, so an
 * inline `outline: none` here silently killed the global
 * `input:focus-visible { outline: 2px solid var(--aps-focus-ring-color) }` ring
 * in globals.css. Measured on the deployed desk: a keyboard-focused
 * #coach-search matched :focus-visible and still computed `outline: none 0px`
 * with its border unmoved — no visible focus indicator at all, on every text
 * field that uses this primitive (WCAG 2.4.7). Every Button on the same screen
 * got the ring, because no Button sets outline inline. Removing the two words
 * restores the same 2px brand ring, at the same 2px offset, that the rest of
 * the product already draws.
 * ========================================================================== */
import React from 'react';
import { font, radius } from '../../styles/tokens';

export interface FieldProps {
  /** Id of the control inside. Drives the label's htmlFor and the hint/error ids. */
  id: string;
  label: React.ReactNode;
  /** Renders the "Required" marker and sets nothing else — validation is the caller's. */
  required?: boolean;
  /** Steady guidance. Stays visible next to an error rather than being replaced by it. */
  hint?: React.ReactNode;
  /** One sentence, present tense, saying what to fix. */
  error?: string | null;
  /** Right of the label — a character count, a "using your program's number" note. */
  accessory?: React.ReactNode;
  /**
   * The body is a GROUP of elements rather than one labelable control (a chosen
   * value with a Change button, a set of checkboxes). Renders a span the caller
   * points `aria-labelledby` at instead of a <label for> that would target a
   * non-labelable element.
   */
  groupLabel?: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

/**
 * The aria props a control inside a Field needs. `hint` is a boolean because
 * the Field owns the hint element; `error` is the message itself so the
 * control is only marked invalid when there is one.
 */
export function fieldAria(
  id: string,
  opts: { hint?: boolean; error?: string | null } = {}
): { id: string; 'aria-describedby'?: string; 'aria-invalid'?: true } {
  const described = [opts.hint ? `${id}-hint` : null, opts.error ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ');
  return {
    id,
    ...(described ? { 'aria-describedby': described } : null),
    ...(opts.error ? { 'aria-invalid': true as const } : null),
  };
}

export const Field: React.FC<FieldProps> = ({
  id,
  label,
  required,
  hint,
  error,
  accessory,
  groupLabel,
  children,
  style,
}) => {
  const labelContent = (
    <>
      {label}
      {required && (
        <span style={{ color: 'var(--aps-text-muted)', fontWeight: font.weight.medium }}> (required)</span>
      )}
    </>
  );
  const labelStyle: React.CSSProperties = {
    fontFamily: font.sans,
    fontSize: font.size.label,
    fontWeight: font.weight.semibold,
    color: 'var(--aps-ink)',
  };
  return (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
      {groupLabel ? (
        <span id={`${id}-label`} style={labelStyle}>{labelContent}</span>
      ) : (
        <label htmlFor={id} style={labelStyle}>{labelContent}</label>
      )}
      {accessory}
    </div>
    {hint && (
      <div
        id={`${id}-hint`}
        style={{
          fontFamily: font.sans,
          fontSize: font.size.labelSm,
          color: 'var(--aps-text-2)',
          lineHeight: 1.5,
        }}
      >
        {hint}
      </div>
    )}
    {children}
    {error && (
      <div
        id={`${id}-error`}
        role="alert"
        style={{
          fontFamily: font.sans,
          fontSize: font.size.labelSm,
          fontWeight: font.weight.semibold,
          color: 'var(--aps-danger)',
          lineHeight: 1.4,
        }}
      >
        {error}
      </div>
    )}
  </div>
  );
};

export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Draws the danger border. Set it from the same message you pass to Field. */
  invalid?: boolean;
  /** A react-feather element rendered inside the left edge. */
  leftIcon?: React.ReactNode;
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  ({ invalid, leftIcon, className, style, ...rest }, ref) => {
    const input = (
      <input
        ref={ref}
        className={['aps-input', className].filter(Boolean).join(' ')}
        aria-invalid={invalid || undefined}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: leftIcon ? '12px 14px 12px 42px' : '12px 14px',
          fontFamily: font.sans,
          fontSize: font.size.body,
          /* Same cascade trap as the outline above: `.aps-input:disabled` in
           * globals.css declares the grey fill and muted ink, and these two
           * inline declarations were quietly outranking it, so a disabled
           * field looked exactly like a live one — the whole invite composer
           * goes disabled while a paste is sending. Reading `disabled` here
           * renders the state the stylesheet already describes. */
          color: rest.disabled ? 'var(--aps-text-muted)' : 'var(--aps-ink)',
          background: rest.disabled ? 'var(--aps-fill)' : 'var(--aps-surface)',
          borderRadius: radius.md,
          transition: 'border-color var(--aps-dur-fast) var(--aps-ease-out)',
          ...style,
        }}
        {...rest}
      />
    );
    if (!leftIcon) return input;
    return (
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 14,
            display: 'flex',
            color: 'var(--aps-text-subtle)',
            pointerEvents: 'none',
          }}
        >
          {leftIcon}
        </span>
        {input}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Draws the danger border. Set it from the same message you pass to Field. */
  invalid?: boolean;
}

/**
 * The multi-line sibling of TextInput, added when the coach verification desk
 * needed a place to paste a convention sheet full of addresses and a place to
 * write down why an account is being rejected. It wears the same `.aps-input`
 * class, so focus, invalid and disabled states are the ones every other control
 * on the product already has — this is the primitive extended, not a second
 * input style entering the codebase.
 */
export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ invalid, className, style, rows = 4, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={['aps-input', className].filter(Boolean).join(' ')}
      aria-invalid={invalid || undefined}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        padding: '12px 14px',
        fontFamily: font.sans,
        fontSize: font.size.body,
        lineHeight: 1.5,
        /* See TextInput: the inline pair outranked `.aps-input:disabled`. */
        color: rest.disabled ? 'var(--aps-text-muted)' : 'var(--aps-ink)',
        background: rest.disabled ? 'var(--aps-fill)' : 'var(--aps-surface)',
        borderRadius: radius.md,
        resize: 'vertical',
        transition: 'border-color var(--aps-dur-fast) var(--aps-ease-out)',
        ...style,
      }}
      {...rest}
    />
  )
);

TextArea.displayName = 'TextArea';

export default Field;
